import React, { useState, useEffect } from 'react';
import { loadDivisionsFor } from '../utils/formationLock';
import ErrorBoundary from '../components/ErrorBoundary';
import { accountsReceivableAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { budgetsAPI, categoriesAPI, businessesAPI } from '../services/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { localDateKey } from '../utils/localDate';
import { parseMoneyInput, MONEY_FIELD_ERROR } from '../utils/money';
import { formatApiError } from '../utils/errorMessage';

const trimAmount = (value) => (value && value.includes('.') ? value.replace(/\.?0+$/, '') : value);

const sameAmount = (a, b) => trimAmount(parseMoneyInput(a).value) === trimAmount(parseMoneyInput(b).value);

// Keeps a draft while typing; saves on blur or Enter, so clearing the field to
// retype works and a bad value gets an inline message instead of being ignored.
function PlannedAmountInput({ budget, onSave }) {
  const [draft, setDraft] = useState(String(budget.planned_amount ?? ''));
  const [fieldError, setFieldError] = useState('');
  const errorId = `planned-amount-error-${budget.budget_id}`;

  useEffect(() => {
    setDraft(String(budget.planned_amount ?? ''));
    setFieldError('');
  }, [budget.planned_amount]);

  const commit = () => {
    const next = parseMoneyInput(draft);
    if (next.error || next.value === null) {
      setFieldError(next.error || MONEY_FIELD_ERROR);
      return;
    }
    setFieldError('');
    if (!sameAmount(next.value, budget.planned_amount)) onSave(budget.budget_id, next.value);
  };

  return (
    <div>
      <input
        type="text"
        inputMode="decimal"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          }
        }}
        aria-label={`Planned amount for ${budget.category_name}`}
        aria-invalid={fieldError ? 'true' : undefined}
        aria-describedby={fieldError ? errorId : undefined}
        className="w-20 lg:w-32 text-right border rounded px-1 lg:px-2 py-1 text-xs lg:text-sm"
      />
      {fieldError && <p id={errorId} className="text-xs text-red-600 mt-1" role="alert">{fieldError}</p>}
    </div>
  );
}

const Budgets = ({ selectedBusiness, divisionScopeIds = null }) => {
  const navigate = useNavigate();
  const isDivisionScoped = Array.isArray(divisionScopeIds) && divisionScopeIds.length > 0;
  const [budgets, setBudgets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [subsidiaries, setSubsidiaries] = useState([]);
  const [subsidiaryFilter, setSubsidiaryFilter] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [viewMode, setViewMode] = useState('monthly'); // 'monthly' or 'category'
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);
  const [formError, setFormError] = useState('');
  const [arProjected, setArProjected] = useState(null);
  const [formData, setFormData] = useState({
    category_id: '',
    budget_year: new Date().getFullYear(),
    budget_month: new Date().getMonth() + 1,
    planned_amount: '',
    state_registration_id: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, [selectedBusiness, selectedYear, subsidiaryFilter, divisionScopeIds]);

  useEffect(() => {
    loadBusinessesAndSubsidiaries();
  }, [selectedBusiness, divisionScopeIds]);

  useEffect(() => {
    if (!isDivisionScoped) return;
    const allowed = divisionScopeIds.map(Number);
    setSubsidiaryFilter((prev) => {
      if (prev && prev !== 'all' && prev !== 'parent' && allowed.includes(Number(prev))) {
        return prev;
      }
      return String(allowed[0]);
    });
    setFormData((prev) => ({
      ...prev,
      state_registration_id: prev.state_registration_id && allowed.includes(Number(prev.state_registration_id))
        ? prev.state_registration_id
        : String(allowed[0])
    }));
  }, [isDivisionScoped, divisionScopeIds]);

  const loadBusinessesAndSubsidiaries = async () => {
    try {
      const busRes = await businessesAPI.getAll();
      setBusinesses(busRes.data);
      
      if (selectedBusiness) {
        const location = (busRes.data || []).find((b) => String(b.id) === String(selectedBusiness));
        const registrations = await loadDivisionsFor(location, businessesAPI.getStateRegistrations);
        let list = registrations.filter(reg => reg.is_subsidiary);
        if (isDivisionScoped) {
          const allowed = new Set(divisionScopeIds.map(Number));
          list = list.filter((reg) => allowed.has(Number(reg.id)));
        }
        setSubsidiaries(list);
      } else {
        setSubsidiaries([]);
      }
    } catch (error) {
      console.error('Failed to load businesses:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const params = { budget_year: selectedYear };
      if (selectedBusiness) params.business_id = selectedBusiness;
      if (subsidiaryFilter && subsidiaryFilter !== 'all' && subsidiaryFilter !== 'parent') {
        params.state_registration_id = subsidiaryFilter;
      }
      
      const [budgetRes, catRes] = await Promise.all([
        budgetsAPI.getWithActuals(params),
        categoriesAPI.getAll({ type: 'expense' }),
      ]);
      
      // If "Parent Business Only" filter is selected, filter out budgets with state_registration_id
      let filteredBudgets = budgetRes.data;
      if (subsidiaryFilter === 'parent') {
        filteredBudgets = budgetRes.data.filter(b => !b.state_registration_id);
      }
      
      setBudgets(filteredBudgets);
      setCategories(catRes.data);

      // Load AR projected income for the next 30 days (rolling window)
      try {
        const start = new Date();
        const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 30);
        const params = {
          ...(selectedBusiness ? { business_id: selectedBusiness } : {}),
          start_date: localDateKey(start), end_date: localDateKey(end)
        };
        const arRes = await accountsReceivableAPI.getProjected(params);
        setArProjected(arRes.data);
      } catch {
        setArProjected(null);
      }
    } catch (error) {
      console.error('Failed to load budgets:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      alert('Failed to load budgets');
    } finally {
      setLoading(false);
    }
  };

  const createBudgetsFor12Months = async () => {
    if (!categories || categories.length === 0) {
      alert('Please create some expense categories first before creating budgets.');
      return;
    }
    
    if (!selectedBusiness) {
      alert('Please select a business first.');
      return;
    }
    
    const monthlyBudgets = [];
    const currentMonth = new Date().getMonth() + 1;
    
    for (let i = 0; i < 12; i++) {
      let month = currentMonth + i;
      let year = selectedYear;
      
      if (month > 12) {
        month -= 12;
        year += 1;
      }
      
      categories.forEach((category) => {
        monthlyBudgets.push({
          business_id: selectedBusiness,
          category_id: category.id,
          budget_year: year,
          budget_month: month,
          planned_amount: 0,
          notes: `Budget for ${category.name}`,
        });
      });
    }
    
    try {
      await budgetsAPI.createBulk({ budgets: monthlyBudgets });
      loadData();
      alert('12-month budget template created successfully!');
    } catch (error) {
      console.error('Failed to create budgets:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      alert(formatApiError(error, 'Failed to create budget template'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    
    // Validate that a business is selected
    if (!selectedBusiness) {
      alert('Please select a business first before creating a budget.');
      return;
    }
    
    // Validate that a category is selected
    if (!formData.category_id) {
      alert('Please select a category.');
      return;
    }
    
    // Validate that planned amount is valid
    const plannedAmount = parseMoneyInput(formData.planned_amount);
    if (plannedAmount.error || plannedAmount.value === null) {
      setFormError(`Planned amount: ${plannedAmount.error || MONEY_FIELD_ERROR}`);
      return;
    }
    
    try {
      const data = {
        business_id: selectedBusiness,
        category_id: parseInt(formData.category_id, 10),
        budget_year: parseInt(formData.budget_year, 10),
        budget_month: parseInt(formData.budget_month, 10),
        planned_amount: plannedAmount.value,
        state_registration_id: formData.state_registration_id || null,
        notes: formData.notes || ''
      };

      if (editingBudget) {
        await budgetsAPI.update(editingBudget.budget_id, data);
      } else {
        await budgetsAPI.create(data);
      }
      
      loadData();
      resetForm();
      alert(editingBudget ? 'Budget updated successfully!' : 'Budget created successfully!');
    } catch (error) {
      console.error('Failed to save budget:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      setFormError(formatApiError(error, 'Failed to save budget'));
    }
  };

  const handleEdit = (budget) => {
    setEditingBudget(budget);
    setFormData({
      category_id: budget.category_id,
      budget_year: budget.budget_year,
      budget_month: budget.budget_month,
      planned_amount: budget.planned_amount,
      state_registration_id: budget.state_registration_id || '',
      notes: budget.notes || ''
    });
    setShowAddModal(true);
  };

  const handleDelete = async (budgetId) => {
    if (!window.confirm('Are you sure you want to delete this budget entry?')) return;
    
    try {
      await budgetsAPI.delete(budgetId);
      loadData();
      alert('Budget deleted successfully!');
    } catch (error) {
      console.error('Failed to delete budget:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      alert('Failed to delete budget');
    }
  };

  const resetForm = () => {
    setFormData({
      category_id: '',
      budget_year: new Date().getFullYear(),
      budget_month: new Date().getMonth() + 1,
      planned_amount: '',
      state_registration_id: '',
      notes: ''
    });
    setEditingBudget(null);
    setFormError('');
    setShowAddModal(false);
  };

  const updateBudgetInline = async (budgetId, plannedAmount) => {
    try {
      const budget = budgets.find((b) => b.budget_id === budgetId);
      await budgetsAPI.update(budgetId, { 
        business_id: budget.business_id,
        category_id: budget.category_id,
        budget_year: budget.budget_year,
        budget_month: budget.budget_month,
        planned_amount: plannedAmount,
        state_registration_id: budget.state_registration_id || null,
        notes: budget.notes || ''
      });
      loadData();
    } catch (error) {
      console.error('Failed to update budget:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      alert('Failed to update budget');
    }
  };

  const copyPreviousMonth = async (year, month) => {
    if (!window.confirm(`Copy budgets from ${getMonthName(month - 1 || 12)} to ${getMonthName(month)}?`)) return;
    
    try {
      const prevMonth = month === 1 ? 12 : month - 1;
      const prevYear = month === 1 ? year - 1 : year;
      
      const prevBudgets = budgets.filter(
        b => b.budget_year === prevYear && b.budget_month === prevMonth
      );
      
      if (prevBudgets.length === 0) {
        alert('No budgets found for previous month');
        return;
      }
      
      const newBudgets = prevBudgets.map(b => ({
        business_id: b.business_id,
        category_id: b.category_id,
        budget_year: year,
        budget_month: month,
        planned_amount: b.planned_amount,
        notes: `Copied from ${getMonthName(prevMonth)}`
      }));
      
      await budgetsAPI.createBulk({ budgets: newBudgets });
      loadData();
      alert('Previous month budgets copied successfully!');
    } catch (error) {
      console.error('Failed to copy budgets:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      alert('Failed to copy previous month budgets');
    }
  };

  const exportBudgets = () => {
    const escapeCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const header = ['Year', 'Month', 'Category', 'Planned', 'Actual'].join(',');
    const rows = budgets.map((budget) => [
      budget.budget_year,
      getMonthName(budget.budget_month),
      escapeCell(budget.category_name),
      budget.planned_amount,
      budget.actual_amount
    ].join(','));
    const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `budget-${selectedYear}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const getMonthName = (month) => {
    return new Date(2000, month - 1, 1).toLocaleDateString('en-US', { month: 'long' });
  };

  const groupedByMonth = budgets.reduce((acc, budget) => {
    const key = `${budget.budget_year}-${budget.budget_month}`;
    if (!acc[key]) {
      acc[key] = {
        year: budget.budget_year,
        month: budget.budget_month,
        budgets: [],
        totalPlanned: 0,
        totalActual: 0,
      };
    }
    acc[key].budgets.push(budget);
    acc[key].totalPlanned = budget.month_total_planned || 0;
    acc[key].totalActual = budget.month_total_actual || 0;
    return acc;
  }, {});

  const groupedByCategory = budgets.reduce((acc, budget) => {
    const key = budget.category_id;
    if (!acc[key]) {
      acc[key] = {
        category_id: budget.category_id,
        category_name: budget.category_name,
        budgets: [],
        totalPlanned: 0,
        totalActual: 0,
      };
    }
    acc[key].budgets.push(budget);
    acc[key].totalPlanned = budget.category_total_planned || 0;
    acc[key].totalActual = budget.category_total_actual || 0;
    return acc;
  }, {});

  const yearStats = {
    totalPlanned: budgets[0]?.year_total_planned || 0,
    totalActual: budgets[0]?.year_total_actual || 0,
    totalVariance: budgets[0]?.year_total_variance || 0,
    averageUsagePercentage: budgets.length > 0 
      ? budgets.reduce((sum, b) => sum + parseFloat(b.variance_percentage || 0), 0) / budgets.length 
      : 0,
    monthsWithBudgets: Object.keys(groupedByMonth).length,
    categoriesWithBudgets: Object.keys(groupedByCategory).length
  };

  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center space-y-4 lg:space-y-0">
        <div>
          <h2 className="text-xl lg:text-2xl font-bold text-gray-900">Budget Planning</h2>
          <p className="text-sm lg:text-base text-gray-600 mt-1">Plan and track your expenses by month and category</p>
        </div>
        <div className="flex flex-wrap gap-2 lg:gap-3 items-center">
          <ErrorBoundary>
            <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded">
              <div className="text-xs text-blue-600">AR Projected (Next 30 Days)</div>
              <div className="text-base lg:text-lg font-semibold text-blue-800">{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(arProjected?.projected_amount || 0)}</div>
            </div>
          </ErrorBoundary>
          <button
            onClick={() => navigate('/categories')}
            className="border px-3 lg:px-4 py-2 rounded-lg text-sm lg:text-base hover:bg-gray-50"
          >
            Manage Categories
          </button>
          <button
            onClick={() => setShowStatsModal(true)}
            className="bg-blue-600 text-white px-3 lg:px-4 py-2 rounded-lg hover:bg-blue-700 transition text-sm lg:text-base"
          >
            📊 Stats
          </button>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="border rounded px-3 lg:px-4 py-2 text-sm lg:text-base"
          >
            {[...Array(5)].map((_, i) => {
              const year = new Date().getFullYear() - 1 + i;
              return (
                <option key={year} value={year}>{year}</option>
              );
            })}
          </select>
          <select
            value={subsidiaryFilter}
            onChange={(e) => setSubsidiaryFilter(e.target.value)}
            className="border rounded px-3 lg:px-4 py-2 text-sm lg:text-base"
            disabled={!selectedBusiness || (isDivisionScoped && subsidiaries.length <= 1)}
          >
            {!isDivisionScoped && <option value="all">All Operations</option>}
            {!isDivisionScoped && <option value="parent">Parent Only</option>}
            {subsidiaries.map((sub) => (
              <option key={sub.id} value={sub.id}>
                {sub.subsidiary_name} ({sub.state})
              </option>
            ))}
          </select>
          <select
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            className="border rounded px-3 lg:px-4 py-2 text-sm lg:text-base"
          >
            <option value="monthly">By Month</option>
            <option value="category">By Category</option>
          </select>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-blue-600 text-white px-4 lg:px-6 py-2 rounded-lg hover:bg-blue-700 transition text-sm lg:text-base"
          >
            + Add
          </button>
          <button
            onClick={exportBudgets}
            className="border px-3 lg:px-4 py-2 rounded-lg text-sm lg:text-base hover:bg-gray-50"
          >
            Export
          </button>
          <button
            onClick={createBudgetsFor12Months}
            disabled={!categories || categories.length === 0 || !selectedBusiness}
            className={`px-3 lg:px-6 py-2 rounded-lg transition text-sm lg:text-base ${
              !categories || categories.length === 0 || !selectedBusiness
                ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
            title={
              !selectedBusiness
                ? 'Please select a business first'
                : !categories || categories.length === 0
                ? 'Please create expense categories first'
                : 'Create budgets for next 12 months'
            }
          >
            <span className="hidden lg:inline">Start from template</span>
            <span className="lg:hidden">Template</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        <div className="bg-white p-4 lg:p-6 rounded-lg shadow">
          <div className="text-xs lg:text-sm text-gray-600">Total Planned</div>
          <div className="text-lg lg:text-2xl font-bold text-blue-600">{formatCurrency(yearStats.totalPlanned)}</div>
        </div>
        <div className="bg-white p-4 lg:p-6 rounded-lg shadow">
          <div className="text-xs lg:text-sm text-gray-600">Total Actual</div>
          <div className="text-lg lg:text-2xl font-bold text-gray-900">{formatCurrency(yearStats.totalActual)}</div>
        </div>
        <div className="bg-white p-4 lg:p-6 rounded-lg shadow">
          <div className="text-xs lg:text-sm text-gray-600">Total Variance</div>
          <div className={`text-lg lg:text-2xl font-bold ${yearStats.totalVariance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatCurrency(yearStats.totalVariance)}
          </div>
        </div>
        <div className="bg-white p-4 lg:p-6 rounded-lg shadow">
          <div className="text-xs lg:text-sm text-gray-600">Avg Usage</div>
          <div className="text-lg lg:text-2xl font-bold text-gray-900">{yearStats.averageUsagePercentage.toFixed(1)}%</div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">Loading budgets...</div>
      ) : budgets.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-600">No budgets created yet. Click "Create 12-Month Budget" or "Add Budget" to get started.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Monthly View */}
          {viewMode === 'monthly' && Object.entries(groupedByMonth)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([key, monthData]) => (
              <div key={key} className="bg-white rounded-lg shadow overflow-hidden">
                <div className="p-4 lg:p-6 border-b bg-gray-50">
                  <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center space-y-2 lg:space-y-0">
                    <h3 className="text-base lg:text-lg font-semibold">
                      {getMonthName(monthData.month)} {monthData.year}
                    </h3>
                    <div className="flex flex-wrap gap-2 lg:gap-4 items-center">
                      <button
                        onClick={() => copyPreviousMonth(monthData.year, monthData.month)}
                        className="text-xs lg:text-sm bg-gray-200 hover:bg-gray-300 px-2 lg:px-3 py-1 rounded transition"
                      >
                        📋 <span className="hidden sm:inline">Copy Previous</span>
                      </button>
                      <div>
                        <span className="text-xs lg:text-sm text-gray-600">Planned: </span>
                        <span className="font-semibold text-xs lg:text-base">{formatCurrency(monthData.totalPlanned)}</span>
                      </div>
                      <div>
                        <span className="text-xs lg:text-sm text-gray-600">Actual: </span>
                        <span className={`font-semibold text-xs lg:text-base ${monthData.totalActual > monthData.totalPlanned ? 'text-red-600' : 'text-green-600'}`}>
                          {formatCurrency(monthData.totalActual)}
                        </span>
                      </div>
                      <div className="hidden sm:block">
                        <span className="text-xs lg:text-sm text-gray-600">Variance: </span>
                        <span className={`font-semibold text-xs lg:text-base ${monthData.totalPlanned - monthData.totalActual >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(monthData.totalPlanned - monthData.totalActual)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[640px]">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-2 lg:px-4 text-xs lg:text-sm">Category</th>
                        <th className="text-right py-2 px-2 lg:px-4 text-xs lg:text-sm">Planned</th>
                        <th className="text-right py-2 px-2 lg:px-4 text-xs lg:text-sm">Actual</th>
                        <th className="text-right py-2 px-2 lg:px-4 text-xs lg:text-sm hidden sm:table-cell">Variance</th>
                        <th className="text-right py-2 px-2 lg:px-4 text-xs lg:text-sm">% Used</th>
                        <th className="text-right py-2 px-2 lg:px-4 text-xs lg:text-sm">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthData.budgets.map((budget) => {
                        const percentUsed = budget.planned_amount > 0 
                          ? (budget.actual_amount / budget.planned_amount * 100).toFixed(1)
                          : 0;
                        
                        return (
                          <tr key={budget.budget_id} className="border-b hover:bg-gray-50">
                            <td className="py-2 lg:py-3 px-2 lg:px-4 text-xs lg:text-sm">{budget.category_name}</td>
                            <td className="text-right py-2 lg:py-3 px-2 lg:px-4">
                              <PlannedAmountInput budget={budget} onSave={updateBudgetInline} />
                            </td>
                            <td className="text-right py-2 lg:py-3 px-2 lg:px-4 text-xs lg:text-sm">{formatCurrency(budget.actual_amount)}</td>
                            <td className={`text-right py-2 lg:py-3 px-2 lg:px-4 font-semibold text-xs lg:text-sm hidden sm:table-cell ${budget.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {formatCurrency(budget.variance)}
                            </td>
                            <td className="text-right py-2 lg:py-3 px-2 lg:px-4">
                              <span className={`px-1 lg:px-2 py-1 rounded text-xs ${
                                percentUsed > 100 ? 'bg-red-100 text-red-800' : 
                                percentUsed > 80 ? 'bg-yellow-100 text-yellow-800' :
                                'bg-green-100 text-green-800'
                              }`}>
                                {percentUsed}%
                              </span>
                            </td>
                            <td className="text-right py-2 lg:py-3 px-2 lg:px-4 space-x-1 lg:space-x-2">
                              <button
                                onClick={() => handleEdit(budget)}
                                className="text-blue-600 hover:text-blue-800 text-xs lg:text-sm"
                              >
                                <span className="hidden sm:inline">Edit</span>
                                <span className="sm:hidden">✏️</span>
                              </button>
                              <button
                                onClick={() => handleDelete(budget.budget_id)}
                                className="text-red-600 hover:text-red-800 text-xs lg:text-sm"
                              >
                                <span className="hidden sm:inline">Delete</span>
                                <span className="sm:hidden">🗑️</span>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}

          {/* Category View */}
          {viewMode === 'category' && Object.entries(groupedByCategory)
            .sort(([, a], [, b]) => a.category_name.localeCompare(b.category_name))
            .map(([key, categoryData]) => (
              <div key={key} className="bg-white rounded-lg shadow overflow-hidden">
                <div className="p-4 lg:p-6 border-b bg-gray-50">
                  <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center space-y-2 lg:space-y-0">
                    <h3 className="text-base lg:text-lg font-semibold">{categoryData.category_name}</h3>
                    <div className="flex flex-wrap gap-2 lg:gap-6">
                      <div>
                        <span className="text-xs lg:text-sm text-gray-600">Total Planned: </span>
                        <span className="font-semibold text-xs lg:text-base">{formatCurrency(categoryData.totalPlanned)}</span>
                      </div>
                      <div>
                        <span className="text-xs lg:text-sm text-gray-600">Total Actual: </span>
                        <span className={`font-semibold text-xs lg:text-base ${categoryData.totalActual > categoryData.totalPlanned ? 'text-red-600' : 'text-green-600'}`}>
                          {formatCurrency(categoryData.totalActual)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[640px]">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-2 lg:px-4 text-xs lg:text-sm">Month</th>
                        <th className="text-right py-2 px-2 lg:px-4 text-xs lg:text-sm">Planned</th>
                        <th className="text-right py-2 px-2 lg:px-4 text-xs lg:text-sm">Actual</th>
                        <th className="text-right py-2 px-2 lg:px-4 text-xs lg:text-sm hidden sm:table-cell">Variance</th>
                        <th className="text-right py-2 px-2 lg:px-4 text-xs lg:text-sm">% Used</th>
                        <th className="text-right py-2 px-2 lg:px-4 text-xs lg:text-sm">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categoryData.budgets
                        .sort((a, b) => `${a.budget_year}-${a.budget_month}`.localeCompare(`${b.budget_year}-${b.budget_month}`))
                        .map((budget) => {
                          const percentUsed = budget.planned_amount > 0 
                            ? (budget.actual_amount / budget.planned_amount * 100).toFixed(1)
                            : 0;
                          
                          return (
                            <tr key={budget.budget_id} className="border-b hover:bg-gray-50">
                              <td className="py-2 lg:py-3 px-2 lg:px-4 text-xs lg:text-sm">{getMonthName(budget.budget_month)} {budget.budget_year}</td>
                              <td className="text-right py-2 lg:py-3 px-2 lg:px-4 text-xs lg:text-sm">{formatCurrency(budget.planned_amount)}</td>
                              <td className="text-right py-2 lg:py-3 px-2 lg:px-4 text-xs lg:text-sm">{formatCurrency(budget.actual_amount)}</td>
                              <td className={`text-right py-2 lg:py-3 px-2 lg:px-4 font-semibold text-xs lg:text-sm hidden sm:table-cell ${budget.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {formatCurrency(budget.variance)}
                              </td>
                              <td className="text-right py-2 lg:py-3 px-2 lg:px-4">
                                <span className={`px-1 lg:px-2 py-1 rounded text-xs ${
                                  percentUsed > 100 ? 'bg-red-100 text-red-800' : 
                                  percentUsed > 80 ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-green-100 text-green-800'
                                }`}>
                                  {percentUsed}%
                                </span>
                              </td>
                              <td className="text-right py-2 lg:py-3 px-2 lg:px-4 space-x-1 lg:space-x-2">
                                <button
                                  onClick={() => handleEdit(budget)}
                                  className="text-blue-600 hover:text-blue-800 text-xs lg:text-sm"
                                >
                                  <span className="hidden sm:inline">Edit</span>
                                  <span className="sm:hidden">✏️</span>
                                </button>
                                <button
                                  onClick={() => handleDelete(budget.budget_id)}
                                  className="text-red-600 hover:text-red-800 text-xs lg:text-sm"
                                >
                                  <span className="hidden sm:inline">Delete</span>
                                  <span className="sm:hidden">🗑️</span>
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
        </div>
      )}

      {/* Add/Edit Budget Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 lg:p-8 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg lg:text-xl font-bold mb-4">
              {editingBudget ? 'Edit Budget' : 'Add Budget'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              {formError && (
                <div className="p-3 bg-red-50 text-red-700 border border-red-200 rounded text-sm" role="alert">{formError}</div>
              )}
              {/* Subsidiary Selector */}
              {selectedBusiness && subsidiaries.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded p-2 lg:p-3 mb-4">
                  <label className="block text-xs lg:text-sm font-medium mb-2">Assign to Business/Subsidiary</label>
                  <select
                    value={formData.state_registration_id}
                    onChange={(e) => setFormData({ ...formData, state_registration_id: e.target.value })}
                    className="w-full border rounded px-2 lg:px-3 py-2 text-sm lg:text-base"
                    required={isDivisionScoped}
                  >
                    {!isDivisionScoped && (
                      <option value="">
                        {businesses.find(b => b.id === parseInt(selectedBusiness))?.name} (Parent Business)
                      </option>
                    )}
                    {subsidiaries.map((sub) => (
                      <option key={sub.id} value={sub.id}>
                        {sub.subsidiary_name} ({sub.state}) - {sub.industry}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-600 mt-1">
                    Select which division/subsidiary this budget belongs to
                  </p>
                </div>
              )}
              
              <div>
                <label className="block text-xs lg:text-sm font-medium mb-1">Category *</label>
                <select
                  value={formData.category_id}
                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                  className="w-full border rounded px-2 lg:px-3 py-2 text-sm lg:text-base"
                  required
                >
                  <option value="">Select Category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs lg:text-sm font-medium mb-1">Year *</label>
                  <input
                    type="number"
                    value={formData.budget_year}
                    onChange={(e) => setFormData({ ...formData, budget_year: parseInt(e.target.value) })}
                    className="w-full border rounded px-2 lg:px-3 py-2 text-sm lg:text-base"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs lg:text-sm font-medium mb-1">Month *</label>
                  <select
                    value={formData.budget_month}
                    onChange={(e) => setFormData({ ...formData, budget_month: parseInt(e.target.value) })}
                    className="w-full border rounded px-2 lg:px-3 py-2 text-sm lg:text-base"
                    required
                  >
                    {[...Array(12)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>{getMonthName(i + 1)}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs lg:text-sm font-medium mb-1">Planned Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.planned_amount}
                  onChange={(e) => setFormData({ ...formData, planned_amount: e.target.value })}
                  className="w-full border rounded px-2 lg:px-3 py-2 text-sm lg:text-base"
                  required
                />
              </div>
              <div>
                <label className="block text-xs lg:text-sm font-medium mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full border rounded px-2 lg:px-3 py-2 text-sm lg:text-base"
                  rows="3"
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 text-sm lg:text-base"
                >
                  {editingBudget ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400 text-sm lg:text-base"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Stats Modal */}
      {showStatsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 lg:p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4 lg:mb-6">
              <h3 className="text-lg lg:text-xl font-bold">Budget Statistics for {selectedYear}</h3>
              <button
                onClick={() => setShowStatsModal(false)}
                className="text-gray-500 hover:text-gray-700 text-xl lg:text-2xl"
              >
                ×
              </button>
            </div>
            <div className="h-64 mb-6">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={Object.values(groupedByMonth).map((group) => ({
                  month: getMonthName(group.month),
                  Planned: group.totalPlanned,
                  Actual: group.totalActual
                }))}>
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="Planned" fill="#2563eb" />
                  <Bar dataKey="Actual" fill="#16a34a" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
              <div className="bg-blue-50 p-3 lg:p-4 rounded">
                <div className="text-xs lg:text-sm text-gray-600">Total Planned</div>
                <div className="text-base lg:text-2xl font-bold">{formatCurrency(yearStats.totalPlanned)}</div>
              </div>
              <div className="bg-gray-50 p-3 lg:p-4 rounded">
                <div className="text-xs lg:text-sm text-gray-600">Total Actual</div>
                <div className="text-base lg:text-2xl font-bold">{formatCurrency(yearStats.totalActual)}</div>
              </div>
              <div className="bg-green-50 p-3 lg:p-4 rounded">
                <div className="text-xs lg:text-sm text-gray-600">Total Variance</div>
                <div className={`text-base lg:text-2xl font-bold ${yearStats.totalVariance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(yearStats.totalVariance)}
                </div>
              </div>
              <div className="bg-purple-50 p-3 lg:p-4 rounded">
                <div className="text-xs lg:text-sm text-gray-600">Average Usage</div>
                <div className="text-base lg:text-2xl font-bold">{yearStats.averageUsagePercentage.toFixed(1)}%</div>
              </div>
              <div className="bg-yellow-50 p-3 lg:p-4 rounded">
                <div className="text-xs lg:text-sm text-gray-600">Months with Budgets</div>
                <div className="text-base lg:text-2xl font-bold">{yearStats.monthsWithBudgets}</div>
              </div>
              <div className="bg-indigo-50 p-3 lg:p-4 rounded">
                <div className="text-xs lg:text-sm text-gray-600">Categories with Budgets</div>
                <div className="text-base lg:text-2xl font-bold">{yearStats.categoriesWithBudgets}</div>
              </div>
            </div>
            <button
              onClick={() => setShowStatsModal(false)}
              className="mt-4 lg:mt-6 w-full bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400 text-sm lg:text-base"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Budgets;
